import * as React from 'react';
import cn from 'classnames';

import { PopupContainer } from 'shared/components/popups';
import {
  PopupEventType,
  PopupPosition,
  PopupRef
} from 'shared/entities/components/Popup';
import { ReferenceProps } from 'shared/newEntities/components/Reference';
import ExternalLink from 'shared/newComponents/ExternalLink';
import {
  TypographyColor,
  TypographySize,
  TypographyType,
  TypographyWeight
} from 'shared/newEntities/components/Typography';
import { CircledQuestionIcon } from 'shared/newComponents/icons';
import { IconSize } from 'shared/entities/components/Icon';
import { useUiStore } from 'stores/index';

import Typography from '../Typography';

import styles from './Reference.modules.scss';

type Props = ReferenceProps & {
  className?: string;
};

const Reference = React.forwardRef<PopupRef, Props>(
  (
    {
      title,
      description,
      docsLink,
      className,
      trigger,
      nested,
      closeOnDocumentClick,
      attachToTriggerOnScroll,
      position = [PopupPosition.topCenter],
      disabled = false,
      titleWeight,
      titleFontSize = TypographySize.xs,
      disabledOnMobile,
      triggerIconProps,
      on = PopupEventType.hover
    }: Props,
    ref
  ) => {
    const uiStore = useUiStore();
    const isNested = Boolean(nested);
    const resolvedCloseOnDocumentClick =
      closeOnDocumentClick !== undefined ? closeOnDocumentClick : !isNested;

    return (
      <PopupContainer
        triggerClassname={cn(styles['icon-container'], className)}
        nested={isNested}
        closeOnDocumentClick={resolvedCloseOnDocumentClick}
        attachToTriggerOnScroll={attachToTriggerOnScroll}
        trigger={
          trigger || (
            <div className={styles.icon}>
              <CircledQuestionIcon
                iconSize={IconSize.S}
                {...triggerIconProps}
              />
            </div>
          )
        }
        on={on}
        position={position}
        ref={ref}
        disabled={
          disabled || (disabledOnMobile ? uiStore.isMobileDevice : false)
        }
      >
        <div
          className={cn(
            styles.popup,
            description && styles['popup_with-description']
          )}
        >
          <Typography
            fontType={TypographyType.text}
            fontSize={titleFontSize}
            fontWeight={titleWeight ?? TypographyWeight.bold}
            className={styles['popup__title']}
          >
            {title}
          </Typography>{' '}
          {docsLink && !description && (
            <ExternalLink
              fontType={TypographyType.text}
              fontSize={TypographySize.xs}
              fontWeight={TypographyWeight.heavy}
              href={docsLink.link}
              className={styles['popup__link']}
              fontColor={null}
            >
              {docsLink.title
                ? docsLink.title
                : (t) =>
                    t('Reference.docsLink', {
                      ns: 'newComponents'
                    })}
            </ExternalLink>
          )}
          {description && (
            <div className={styles['popup__description']}>
              <Typography
                Element="span"
                fontType={TypographyType.text}
                fontSize={TypographySize.xs}
                fontWeight={TypographyWeight.heavy}
                fontColor={TypographyColor.secondary}
              >
                {description}
              </Typography>{' '}
              {docsLink && (
                <ExternalLink
                  fontType={TypographyType.text}
                  fontSize={TypographySize.xs}
                  fontWeight={TypographyWeight.heavy}
                  href={docsLink.link}
                  className={styles['popup__link']}
                  fontColor={null}
                >
                  {docsLink.title
                    ? docsLink.title
                    : (t) =>
                        t('Reference.docsLink', {
                          ns: 'newComponents'
                        })}
                </ExternalLink>
              )}
            </div>
          )}
        </div>
      </PopupContainer>
    );
  }
);

export default React.memo(Reference);
